// Wrappers for the Skill Builder endpoints, mounted under `/bmd/*` in
// tutor-backend alongside the base BMD and prerequisite routes.
//
// Use the shared `apiClient` from `src/infra/api-client.ts`, not raw axios: it
// handles Bearer-token injection and the expired-token redirect.

import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type {
  BmdSkillbuilderCreatePayload,
  BmdSkillbuilderPreviewResponse,
  BmdSkillbuilderCreateResponse,
  BmdSkillbuilderPickerRequest,
  BmdSkillbuilderPickerResponse,
} from "../types/bmd-skillbuilder.types"

const PICKER_ENDPOINT = "bmd/skillbuilderPicker"
const CREATE_ENDPOINT = "bmd/createSkillbuilderAssignment"
const PREVIEW_ENDPOINT = "bmd/skillbuilderPreview"
const FILE_ENDPOINT = "bmd/skillbuilderFile"

const EMPTY_COUNTS = {
  failed_skills: 0,
  with_skillbuilder: 0,
  without_skillbuilder: 0,
  assigned: 0,
  completed: 0,
  needs_tutor: 0,
  excluded_fundamental: 0,
}

// Only reached if the server omitted the field. The real mark comes from the
// response, and each row carries the mark it was actually judged against.
const FALLBACK_PASS_PERCENT = 75

/** The Skill Builders owed for one student and one diagnostic. Read-only. */
export async function getBmdSkillbuilderPicker(
  req: BmdSkillbuilderPickerRequest,
  signal?: AbortSignal
): Promise<BmdSkillbuilderPickerResponse> {
  const response = await apiClient.post<BmdSkillbuilderPickerResponse>(
    BASE_URL + PICKER_ENDPOINT,
    {
      diagnostics_session_key: req.diagnostics_session_key,
      student_key: req.student_key,
    },
    { signal }
  )
  const data = response.data
  return {
    ...data,
    session_keys: Array.isArray(data?.session_keys) ? data.session_keys : [],
    counts: data?.counts || EMPTY_COUNTS,
    pass_percent: data?.pass_percent ?? FALLBACK_PASS_PERCENT,
    years: Array.isArray(data?.years) ? data.years : [],
  }
}

/** Adds one Skill Builder to the lesson as its own activity. */
export async function createBmdSkillbuilderAssignment(
  payload: BmdSkillbuilderCreatePayload,
  signal?: AbortSignal
): Promise<BmdSkillbuilderCreateResponse> {
  const response = await apiClient.post<BmdSkillbuilderCreateResponse>(
    BASE_URL + CREATE_ENDPOINT,
    {
      diagnostics_session_key: payload.diagnostics_session_key,
      skillbuilder_id: payload.skillbuilder_id,
      session_key: payload.session_key,
      student_key: payload.student_key,
      lesson_key: payload.lesson_key || "",
      is_homework: payload.is_homework ?? 0,
    },
    { signal }
  )
  return response.data
}

/** Where a Skill Builder's PDF is, and whether it can be auto-marked. */
/**
 * `activityKey` scopes the preview to something ASSIGNED to a student, so the
 * response carries their attempt. Without it the preview is of the worksheet
 * itself, which is what the picker wants while choosing one.
 */
export async function getBmdSkillbuilderPreview(
  skillbuilderId: number,
  signal?: AbortSignal,
  activityKey?: string | null
): Promise<BmdSkillbuilderPreviewResponse> {
  const response = await apiClient.post<BmdSkillbuilderPreviewResponse>(
    BASE_URL + PREVIEW_ENDPOINT,
    activityKey
      ? { pk_lesson_activity_key: activityKey }
      : { skillbuilder_id: skillbuilderId },
    { signal }
  )
  return response.data
}

/**
 * The worksheet PDF bytes, streamed through the API.
 *
 * Not fetched from the asset store directly. That needs a CORS rule on the
 * bucket, and a download manager extension will hijack any URL ending in .pdf
 * and break the fetch with nothing useful in the console. Going through the API
 * avoids both, and reuses the auth the page already has.
 */
/**
 * With `activityKey`, the server returns the STUDENT'S marked attempt when there
 * is one: their answers, with a tick or a cross on each box. `withAnswers` only
 * applies to the un-attempted worksheet, where there is nothing else to show.
 */
export async function getBmdSkillbuilderPdf(
  skillbuilderId: number,
  withAnswers = false,
  signal?: AbortSignal,
  activityKey?: string | null
): Promise<Uint8Array> {
  const response = await apiClient.post(
    BASE_URL + FILE_ENDPOINT,
    {
      ...(activityKey
        ? { pk_lesson_activity_key: activityKey }
        : { skillbuilder_id: skillbuilderId }),
      // Filled in from the worksheet's own key, server-side. The answers are
      // never sent to the student app.
      with_answers: withAnswers ? "1" : "0",
    },
    { signal, responseType: "arraybuffer" }
  )
  return new Uint8Array(response.data as ArrayBuffer)
}
